import React from 'react';
import { Button, SparkIcon, Text } from '@spark';
import {
    FIELD_LABELS,
    fieldHasEye,
    fieldSubLabel,
    formatChangeValue,
    promptDiffRows,
} from '../../utils/revaluateFlow';

const SCHEMA_FIELDS = new Set(['teacher_schema', 'student_schema']);

function DiffRow({ row }) {
    if (row.kind === 'replaced') {
        return (
            <span className="revaluate-diff__row revaluate-diff__row--replaced">
                <span className="revaluate-diff__removed">{row.removed || ' '}</span>
                {' '}
                <span className="revaluate-diff__added">{row.added || ' '}</span>
            </span>
        );
    }
    return (
        <span className={`revaluate-diff__row revaluate-diff__row--${row.kind}`}>
            {row.kind === 'removed' ? <span className="revaluate-diff__removed">{row.text || ' '}</span> : null}
            {row.kind === 'added' ? <span className="revaluate-diff__added">{row.text || ' '}</span> : null}
            {row.kind === 'same' ? row.text || ' ' : null}
        </span>
    );
}

/**
 * Full prompt diff (Figma 134:12848): a replaced line shows the old text
 * struck through next to the new text; added lines are tinted; unchanged
 * lines stay as context.
 */
export function RevaluateDiffView({ previous, current }) {
    const rows = promptDiffRows(previous, current);
    return (
        <div className="revaluate-diff spark-text-detail-regular" aria-label="Prompt diff">
            {rows.length === 0 ? (
                <Text variant="base-regular" color="secondary">No changes.</Text>
            ) : (
                rows.map((row, index) => <DiffRow key={`${row.kind}-${index}`} row={row} />)
            )}
        </div>
    );
}

function ValueChange({ previous, current }) {
    return (
        <span className="revaluate-changes__values">
            <Text as="span" variant="base-regular" color="primary">{previous}</Text>
            <SparkIcon slug="arrow-right" size="small" className="revaluate-changes__arrow" />
            <Text as="span" variant="base-regular" color="primary">{current}</Text>
        </span>
    );
}

function PromptChange({ row, onOpenDiff }) {
    const previous = formatChangeValue(row.previous);
    const current = formatChangeValue(row.current);
    return (
        <>
            <Text as="span" variant="base-regular" color="primary" className="revaluate-changes__previous">
                {previous === '—' ? 'Off' : previous}
            </Text>
            <Text as="span" variant="base-regular" color="primary" className="revaluate-changes__current">
                {current === '—' ? 'Off' : current}
            </Text>
            <Button variant="ghost" size="tiny" type="button" onClick={() => onOpenDiff(row.field)}>
                Show more
            </Button>
        </>
    );
}

/**
 * Changed-settings table (Figma 134:12383): Setting | Change | eye. Prompt
 * rows preview the old and new text; model, schema and comparison rows show
 * "previous → current".
 */
export default function RevaluateChangesView({ rows, onOpenDiff }) {
    if (!rows.length) {
        return (
            <Text as="p" variant="base-regular" color="secondary">No changes.</Text>
        );
    }
    return (
        <div className="revaluate-changes" role="table" aria-label="Changed settings">
            <div className="revaluate-changes__row revaluate-changes__row--head" role="row">
                <Text as="span" variant="base-medium" color="primary" role="columnheader" className="revaluate-changes__setting">
                    Setting
                </Text>
                <Text as="span" variant="base-medium" color="primary" role="columnheader" className="revaluate-changes__change">
                    Change
                </Text>
                <span className="revaluate-changes__action" aria-hidden="true" />
            </div>
            {rows.map((row) => {
                const label = FIELD_LABELS[row.field] || row.field;
                const eye = fieldHasEye(row.field);
                const isPrompt = eye && !SCHEMA_FIELDS.has(row.field);
                return (
                    <div key={row.field} className="revaluate-changes__row" role="row">
                        <div className="revaluate-changes__setting" role="cell">
                            <Text as="span" variant="base-medium" color="primary">{label}</Text>
                            <Text as="span" variant="detail-regular" color="secondary">
                                {fieldSubLabel(row.field, row.previous, row.current)}
                            </Text>
                        </div>
                        <div className="revaluate-changes__change" role="cell">
                            {isPrompt ? (
                                <PromptChange row={row} onOpenDiff={onOpenDiff} />
                            ) : (
                                <ValueChange
                                    previous={formatChangeValue(row.previous, row.field)}
                                    current={formatChangeValue(row.current, row.field)}
                                />
                            )}
                        </div>
                        <div className="revaluate-changes__action" role="cell">
                            {eye ? (
                                <Button
                                    variant="tertiary"
                                    size="tiny"
                                    type="button"
                                    icon={<SparkIcon slug="eye" size="small" />}
                                    aria-label={`View ${label}`}
                                    onClick={() => onOpenDiff(row.field)}
                                />
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
